import { Injectable, signal, type Signal, type Type } from '@angular/core';

type StoreOptions = { provideInRoot?: boolean };

/** Factory to generate stores
 *  Example:
 *  ```typescript
 *  const CountStore = signalStore({count: 0})
 *    .create((state, patchState) => ({
 *      count: computed(() => state().count),
 *      increment: () => patchState(state => ({count: state.count + 1}))
 *    }));
 *  ```
 *
 *  Usage
 *  ```typescript
 *  const store = inject(CountStore);
 *  const count = store.count;
 *  store.increment();
 * */
export const signalStore = <T extends object>(
  initState: T,
  opts: StoreOptions = {},
) => {
  const store = signal(initState);

  const patch = (partial: Partial<T> | ((current: T) => T)) => {
    if (typeof partial === 'function') {
      store.update(partial);
    } else {
      store.update((current) => ({ ...current, ...partial }));
    }
  };

  return {
    create: <R extends Record<string, unknown>>(
      cb: (state: Signal<T>, patchState: typeof patch) => R,
    ) => {
      type Context = R & {
        reset: () => void;
      };
      @Injectable({
        providedIn: opts.provideInRoot === false ? null : 'root',
      })
      class Store {
        [key: string]: unknown;
        constructor() {
          const context = {
            ...cb(store.asReadonly(), patch),
            reset: () => store.set(initState),
          };
          for (const key in context) {
            (this as Record<string, unknown>)[key] = context[key];
          }
        }
      }
      return Store as unknown as Type<Context>;
    },
  };
};
